import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import {
  Card,
  Descriptions,
  Table,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Tag,
  Spin,
  Select,
  App,
  Popconfirm,
  InputNumber,
  Modal,
  Divider,
  Collapse,
  Alert,
  Dropdown,
  Input,
  DatePicker,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  SendOutlined,
  AccountBookOutlined,
  DollarOutlined,
  StopOutlined,
  PrinterOutlined,
  FileTextOutlined,
  QrcodeOutlined,
  DownOutlined,
} from '@ant-design/icons';
import QRCode from 'qrcode.react';
import complianceService, { type InvoiceExchangeTransmission } from '@/services/compliance.service';
import ReactDOM from 'react-dom/client';
import { useSystemSettings } from '@/hooks/useSettings';
import { PrintableSalesInvoice, PrintableVoucher, type SalesInvoicePrintVariant } from '@/components/sales-invoice';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { approvalService } from '@/services/approval.service';
import salesInvoiceService from '../../services/salesInvoice.service';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useSalesInvoice,
  useSalesInvoiceItems,
  useUpdateSalesInvoice,
  useUpdateSalesInvoiceAddresses,
  useUpdateSalesInvoiceGlAccounts,
  useApproveSalesInvoice,
  usePostSalesInvoice,
  usePostSalesInvoiceToGL,
  useUnpostSalesInvoiceFromGL,
  useCancelSalesInvoice,
  useChartAccountsByType,
  useAdvanceAllocations,
  useCreditNoteAllocations,
  useOpenAdvances,
  useOpenCreditNotes,
  useApplyAdvances,
  useApplyCreditNotes,
} from '../../hooks/useSalesInvoice';
import {
  SalesInvoice,
  SalesInvoiceItem,
  SalesInvoiceGlAccounts,
  ApplyAdvanceAllocationInput,
  OpenAdvanceRow,
} from '../../services/salesInvoice.service';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCurrency } from '@/contexts/CurrencyContext';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { TaskRoutingCard } from '@/components/unified/TaskRoutingCard';
import { ENTITY_TYPE_SALES_INVOICE } from '@/constants/approval-entity-types';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import InheritedAttachmentsCard from '@/components/documents/InheritedAttachmentsCard';
import {
  DOCUMENT_ENTITY_TYPE_SALES_INVOICE,
  SCM_ATTACHMENT_UPLOAD_HINT,
} from '@/constants/document-entity-types';

const { Title, Text } = Typography;
const { TextArea } = Input;

function invoiceStatusLabel(status: string, t: (key: string) => string): string {
  const map: Record<string, string> = {
    Draft: t('statusDraft'),
    Approved: t('statusApproved'),
    Sent: t('statusSent'),
    Paid: t('statusPaid'),
    Cancelled: t('statusCancelled'),
    Submitted: t('statusSubmitted'),
  };
  return map[status] ?? status;
}

function paymentStatusLabel(status: string, t: (key: string) => string): string {
  const map: Record<string, string> = {
    Unpaid: t('unpaid'),
    'Partially Paid': t('partiallyPaid'),
    Paid: t('paid'),
  };
  return map[status] ?? status;
}

function invoiceTypeLabel(type: string | undefined | null, t: (key: string) => string): string {
  if (!type) return t('standard');
  if (type === 'Advance') return t('advance');
  if (type === 'Standard') return t('standard');
  return type;
}

function composeAddressFromParts(parts: Array<string | null | undefined>): string {
  return parts.map((p) => String(p ?? '').trim()).filter(Boolean).join(', ');
}

function resolveInvoiceBillingEditorValue(inv: SalesInvoice): string {
  const snap = String(inv.customer_address ?? '').trim();
  if (snap) return snap;
  const c = inv.customer;
  if (!c) return '';
  return composeAddressFromParts([
    c.billing_address_line1,
    c.billing_address_line2,
    c.billing_city,
    c.billing_state,
    c.billing_country,
    c.billing_postal_code,
  ]);
}

function resolveInvoiceShippingEditorValue(inv: SalesInvoice): string {
  const snap = String(inv.shipping_address ?? '').trim();
  if (snap) return snap;
  const c = inv.customer;
  if (!c) return '';
  return composeAddressFromParts([
    c.shipping_address_line1,
    c.shipping_address_line2,
    c.shipping_city,
    c.shipping_state,
    c.shipping_country,
    c.shipping_postal_code,
  ]);
}

const SalesInvoiceDetailPage: React.FC = () => {
  const { t } = useTranslation('growth');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();
  const [siSubmitLoading, setSiSubmitLoading] = useState(false);

  const { data: settings } = useSystemSettings();
  const { data: invoice, isLoading: loadingInvoice } = useSalesInvoice(id ?? '');
  const { data: departments = [] } = useDepartmentsForSelection();
  const { data: itemsData, isLoading: loadingItems } = useSalesInvoiceItems(id ?? '');
  const updateMutation = useUpdateSalesInvoice();
  const updateAddressesMutation = useUpdateSalesInvoiceAddresses();
  const updateGlAccountsMutation = useUpdateSalesInvoiceGlAccounts();
  const approveMutation = useApproveSalesInvoice();
  const [billingAddressDraft, setBillingAddressDraft] = useState('');
  const [shippingAddressDraft, setShippingAddressDraft] = useState('');
  const [addressesDirty, setAddressesDirty] = useState(false);
  const postMutation = usePostSalesInvoice();
  const postToGLMutation = usePostSalesInvoiceToGL();
  const unpostFromGLMutation = useUnpostSalesInvoiceFromGL();
  const cancelMutation = useCancelSalesInvoice();

  const { data: incomeAccounts = [] } = useChartAccountsByType('Income', false);
  const { data: assetAccounts = [] } = useChartAccountsByType('Asset', false);
  const { data: liabilityAccounts = [] } = useChartAccountsByType('Liability', false);

  const items: SalesInvoiceItem[] = Array.isArray(itemsData) ? itemsData : (itemsData as any)?.data ?? [];

  const [localInvoice, setLocalInvoice] = useState<Partial<SalesInvoice> | null>(null);
  const [localItems, setLocalItems] = useState<Partial<SalesInvoiceItem>[]>([]);
  const [defaultRevenueAccountId, setDefaultRevenueAccountId] = useState<string>('');
  const [applyAdvanceModalVisible, setApplyAdvanceModalVisible] = useState(false);
  const [applyCreditNoteModalVisible, setApplyCreditNoteModalVisible] = useState(false);
  const [advanceAmounts, setAdvanceAmounts] = useState<Record<string, number>>({});
  const [creditNoteAmounts, setCreditNoteAmounts] = useState<Record<string, number>>({});
  const [uaeEinvoiceLoading, setUaeEinvoiceLoading] = useState(false);
  const [uaeQrModalVisible, setUaeQrModalVisible] = useState(false);

  const { data: uaeTransmissions = [], refetch: refetchUaeTransmissions } = useQuery({
    queryKey: ['uae-einvoice-transmissions', id],
    queryFn: () =>
      id
        ? complianceService.listTransmissions({ document_type: 'SalesInvoice', document_id: id })
        : Promise.resolve([] as InvoiceExchangeTransmission[]),
    enabled: !!id,
  });
  const latestUaeTransmission = uaeTransmissions[0];

  const { data: uaeReadiness, isLoading: uaeReadinessLoading, isError: uaeReadinessError, error: uaeReadinessQueryError } = useQuery({
    queryKey: ['uae-einvoice-readiness', id],
    queryFn: () => (id ? complianceService.getSalesInvoiceReadiness(id) : Promise.reject()),
    enabled: !!id && !!invoice?.gl_posted && invoice?.invoice_status !== 'Cancelled',
  });
  const uaeGenerateDisabled =
    uaeReadinessLoading ||
    uaeReadinessError ||
    (uaeReadiness != null && !uaeReadiness.ready && latestUaeTransmission?.status !== 'Cleared');

  const showSettlement = Boolean(
    invoice && invoice.invoice_status !== 'Cancelled' && invoice.customer_id
  );
  const settlementSourcesCustomerId =
    invoice && showSettlement && invoice.invoice_type !== 'Advance' ? (invoice.customer_id ?? null) : null;
  const { data: advanceAllocations = [] } = useAdvanceAllocations(
    id ?? null,
    showSettlement
  );
  const { data: creditNoteAllocations = [] } = useCreditNoteAllocations(
    id ?? null,
    showSettlement
  );
  const { data: openAdvances = [], isLoading: loadingOpenAdvances } = useOpenAdvances(
    settlementSourcesCustomerId
  );
  const { data: openCreditNotes = [], isLoading: loadingOpenCreditNotes } = useOpenCreditNotes(
    settlementSourcesCustomerId
  );
  const applyAdvancesMutation = useApplyAdvances();
  const applyCreditNotesMutation = useApplyCreditNotes();

  const breadcrumbSegments = React.useMemo(
    () =>
      invoice
        ? [
            { title: t('growthRevenue'), path: '/growth/dashboard' },
            { title: t('taxInvoicesBreadcrumb'), path: '/growth/invoices' },
            { title: invoice.invoice_number || t('invoice') },
          ]
        : null,
    [invoice?.id, invoice?.invoice_number, t]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const isDraft = (invoice?.invoice_status ?? '') === 'Draft';
  const isEditMode = isDraft && (localInvoice !== null || localItems.length > 0);

  const displayInvoice = useMemo(() => ({ ...invoice, ...localInvoice }), [invoice, localInvoice]);

  useEffect(() => {
    if (!invoice || addressesDirty) return;
    setBillingAddressDraft(resolveInvoiceBillingEditorValue(invoice));
    setShippingAddressDraft(resolveInvoiceShippingEditorValue(invoice));
  }, [invoice?.id, invoice?.customer_address, invoice?.shipping_address, invoice?.customer, addressesDirty]);

  const { data: revenueServiceTypesSi = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as { data?: unknown })?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray((body as { data?: unknown })?.data) ? (body as { data: { id: string; service_code: string; service_name: string }[] }).data : [];
      return list as { id: string; service_code: string; service_name: string }[];
    },
  });
  const invoiceRstLabel = useMemo(() => {
    const inv = displayInvoice as SalesInvoice;
    const rid = inv?.revenue_service_type_id;
    if (!rid) return undefined;
    const r = revenueServiceTypesSi.find((x) => x.id === rid);
    return r ? `${r.service_code} — ${r.service_name}` : undefined;
  }, [displayInvoice, revenueServiceTypesSi]);
  const displayItems = useMemo(() => {
    if (localItems.length > 0) return localItems;
    return items;
  }, [items, localItems]);

  /** Align UI with server rule: grand_total − paid − advance − credit notes (avoids stale balance_due). */
  const computedBalanceDue = useMemo(() => {
    const inv = displayInvoice as SalesInvoice;
    if (!inv || inv.grand_total === undefined || inv.grand_total === null) return null;
    const gt = Number(inv.grand_total ?? 0);
    const paid = Number(inv.paid_amount ?? 0);
    const adv = Number(inv.advance_adjusted_amount ?? 0);
    const cn = Number(inv.credit_note_adjusted_amount ?? 0);
    return Math.max(0, gt - paid - adv - cn);
  }, [displayInvoice]);

  const settlementPendingInfo = useMemo(() => {
    if (!settlementSourcesCustomerId || !invoice) {
      return {
        showAlert: false,
        remainingCreditHeadroom: 0,
        openAdvanceTotal: 0,
        openCreditNoteTotal: 0,
        advanceSourceCount: 0,
        creditNoteSourceCount: 0,
      };
    }
    const inv = displayInvoice as SalesInvoice;
    const gt = Number(inv.grand_total ?? 0);
    const paid = Number(inv.paid_amount ?? 0);
    const adv = Number(inv.advance_adjusted_amount ?? 0);
    const cn = Number(inv.credit_note_adjusted_amount ?? 0);
    const remainingCreditHeadroom = Math.max(0, gt - paid - adv - cn);
    const eps = 0.01;
    const openAdvanceTotal = openAdvances.reduce((s, r) => s + Number(r.open_balance ?? 0), 0);
    const openCreditNoteTotal = openCreditNotes.reduce((s, r) => s + Number(r.open_balance ?? 0), 0);
    const showAlert =
      remainingCreditHeadroom > eps && (openAdvanceTotal > eps || openCreditNoteTotal > eps);
    return {
      showAlert,
      remainingCreditHeadroom,
      openAdvanceTotal,
      openCreditNoteTotal,
      advanceSourceCount: openAdvances.filter((r) => Number(r.open_balance ?? 0) > eps).length,
      creditNoteSourceCount: openCreditNotes.filter((r) => Number(r.open_balance ?? 0) > eps).length,
    };
  }, [settlementSourcesCustomerId, invoice, displayInvoice, openAdvances, openCreditNotes]);

  const siWorkflowStages = React.useMemo(() => {
    if (!invoice) return [];
    const st = invoice.invoice_status;
    const cancelled = st === 'Cancelled';
    const gl = !!invoice.gl_posted;
    const paid = invoice.payment_status === 'Paid';
    const settlementDesc = settlementPendingInfo.showAlert
      ? t('settlementOpenAvailable')
      : t('settlementAdvancesCreditNotes');
    return [
      {
        title: t('invoice'),
        description: st ? invoiceStatusLabel(st, t) : st,
        status: (cancelled ? 'error' : st === 'Draft' ? 'process' : 'finish') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: t('workflowGlPosting'),
        description: gl ? t('posted') : t('pending'),
        status: (cancelled ? 'wait' : gl ? 'finish' : st !== 'Draft' ? 'process' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: t('workflowSettlement'),
        description: settlementDesc,
        status: (st === 'Draft' || cancelled ? 'wait' : 'process') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: t('workflowPayment'),
        description: paid ? t('paid') : t('open'),
        status: (paid ? 'finish' : cancelled ? 'wait' : 'process') as 'wait' | 'process' | 'finish' | 'error',
      },
    ];
  }, [invoice, settlementPendingInfo.showAlert, t]);

  const siWorkflowCurrent = React.useMemo(() => {
    if (!invoice) return 0;
    if (invoice.invoice_status === 'Draft') return 0;
    if (invoice.invoice_status === 'Cancelled') return 0;
    if (!invoice.gl_posted) return 1;
    if (invoice.payment_status !== 'Paid') return 2;
    return 3;
  }, [invoice]);

  React.useEffect(() => {
    if (invoice) setLocalInvoice(null);
  }, [invoice?.id]);

  React.useEffect(() => {
    if (items.length > 0 && localItems.length === 0) setLocalItems([]);
  }, [items.length]);

  const handleApplyDefaultRevenue = () => {
    if (!defaultRevenueAccountId) {
      message.warning(t('selectDefaultRevenueFirst'));
      return;
    }
    const next = displayItems.map((line) => ({
      ...line,
      revenue_account_id: defaultRevenueAccountId,
    }));
    setLocalItems(next);
    message.success(t('appliedToAllLines'));
  };

  const handleLineRevenueChange = (lineIndex: number, accountId: string) => {
    const next = [...displayItems];
    if (next[lineIndex]) next[lineIndex] = { ...next[lineIndex], revenue_account_id: accountId };
    setLocalItems(next);
  };

  const handleSave = async () => {
    if (!id) return;
    const currentDept = (localInvoice?.department_id ?? invoice?.department_id) ?? undefined;
    if (isDraft && !currentDept) {
      message.error(t('departmentRequired'));
      return;
    }
    const invPayload =
      localInvoice && Object.keys(localInvoice).length > 0
        ? localInvoice
        : currentDept
          ? { department_id: currentDept }
          : undefined;
    const itemsPayload =
      localItems.length > 0
        ? displayItems.map((loc, idx) => ({ ...(items[idx] ?? {}), ...loc }))
        : undefined;
    if (!invPayload && !itemsPayload) return;
    try {
      await updateMutation.mutateAsync({
        id,
        invoice: invPayload ?? {},
        items: itemsPayload ?? items,
      });
      setLocalInvoice(null);
      setLocalItems([]);
    } catch (e) {
      // error handled in mutation
    }
  };

  const handlePrint = async (printVariant: SalesInvoicePrintVariant = 'preprinted') => {
    if (!invoice) return;
    const companySettings = settings?.filter((s) => s.category === 'company') || [];
    const getSetting = (key: string) => companySettings.find((s) => s.setting_key === key)?.setting_value ?? '';
    const companyInfo = {
      name: getSetting('company_name') || t('companyNameFallback'),
      address: getSetting('company_address') || '',
      phone: getSetting('company_phone') || '',
      email: getSetting('company_email') || '',
      taxNumber: getSetting('tax_registration_number') ? `TRN: ${getSetting('tax_registration_number')}` : '',
      bankName: getSetting('bank_name') || '',
      bankAccountNo: getSetting('bank_account_no') || getSetting('bank_account_number') || '',
      bankIban: getSetting('bank_iban') || getSetting('iban') || '',
      bankRoutingCode: getSetting('bank_routing_code') || getSetting('routing_code') || '',
      bankSwiftCode: getSetting('bank_swift_code') || getSetting('swift_code') || '',
      bankAddress: getSetting('bank_address') || '',
      logo: getSetting('company_logo') || undefined,
    };

    let approvalSigners: Awaited<ReturnType<typeof approvalService.getPrintSigners>> = [];
    if (invoice.approval_instance_id) {
      try {
        approvalSigners = await approvalService.getPrintSigners('sales_invoice', invoice.id);
      } catch {
        approvalSigners = [];
      }
    }

    const mainContent = document.getElementById('root');
    if (mainContent) mainContent.style.display = 'none';
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.display = 'block';
    printContainer.style.width = '8.5in';
    printContainer.style.margin = '0 auto';
    document.body.appendChild(printContainer);
    const cleanup = () => {
      root.unmount();
      document.body.removeChild(printContainer);
      if (mainContent) mainContent.style.display = 'block';
      window.removeEventListener('afterprint', cleanup);
    };
    const root = ReactDOM.createRoot(printContainer);
    root.render(
      <PrintableSalesInvoice
        invoice={invoice}
        items={displayItems as SalesInvoiceItem[]}
        revenueServiceTypeLabel={invoiceRstLabel}
        companyInfo={companyInfo}
        approvalSigners={approvalSigners}
        qrImageDataUrl={latestUaeTransmission?.qr_image_data_url}
        clearanceNumber={latestUaeTransmission?.clearance_number}
        eInvoiceUuid={latestUaeTransmission?.exchange_uuid || invoice.e_invoice_uuid}
        printVariant={printVariant}
        onClose={cleanup}
      />
    );
    window.addEventListener('afterprint', cleanup);
  };

  const handlePrintVoucher = async () => {
    if (!id) return;
    try {
      const voucher = await salesInvoiceService.getVoucherBySalesInvoiceId(id);
      if (!voucher) {
        message.warning(t('noVoucherFoundPostFirst'));
        return;
      }
      const companySettings = settings?.filter((s) => s.category === 'company') || [];
      const companyName = companySettings.find((s) => s.setting_key === 'company_name')?.setting_value ?? '';
      const mainContent = document.getElementById('root');
      if (mainContent) mainContent.style.display = 'none';
      const printContainer = document.createElement('div');
      printContainer.id = 'print-container';
      printContainer.style.display = 'block';
      document.body.appendChild(printContainer);
      const cleanup = () => {
        root.unmount();
        document.body.removeChild(printContainer);
        if (mainContent) mainContent.style.display = 'block';
        window.removeEventListener('afterprint', cleanup);
      };
      const root = ReactDOM.createRoot(printContainer);
      root.render(
        <PrintableVoucher
          voucher={voucher}
          referenceLabel={t('taxInvoiceLabel')}
          companyName={companyName}
          onClose={cleanup}
        />
      );
      window.addEventListener('afterprint', cleanup);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || t('failedToLoadVoucher'));
    }
  };

  if (loadingInvoice && !invoice) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
      {id ? <AiPageContextSync module="sales" docType="SalesInvoice" docId={id} /> : null}
        <Spin size="large" />
      </div>
    );
  }
  if (!invoice) {
    return (
      <Card>
        <Text type="secondary">{t('invoiceNotFound')}</Text>
      </Card>
    );
  }

  const inv = displayInvoice as SalesInvoice;
  const invCurrency = inv.currency;
  const fmt = (amount: number) => formatCurrency(amount, invCurrency);
  const canEdit = isDraft;
  const canEditAddresses = inv.invoice_status !== 'Cancelled';
  const canEditGlAccounts = !inv.gl_posted && inv.invoice_status !== 'Cancelled';

  const resolveGlAccountsPayload = (): SalesInvoiceGlAccounts => {
    const pick = (key: keyof SalesInvoiceGlAccounts): string | null | undefined => {
      const localVal = localInvoice?.[key as keyof SalesInvoice];
      if (localVal !== undefined) {
        return localVal != null && String(localVal).trim() ? String(localVal).trim() : null;
      }
      const invVal = inv[key as keyof SalesInvoice];
      return invVal != null && String(invVal).trim() ? String(invVal).trim() : null;
    };
    return {
      ar_account_id: pick('ar_account_id'),
      vat_account_id: pick('vat_account_id'),
      advance_adjustment_account_id: pick('advance_adjustment_account_id'),
      retention_account_id: pick('retention_account_id'),
      prepaid_customer_advance_account_id: pick('prepaid_customer_advance_account_id'),
      advance_from_customer_account_id: pick('advance_from_customer_account_id'),
    };
  };

  const handleSaveGlAccounts = () => {
    if (!id) return;
    updateGlAccountsMutation.mutate(
      { id, ...resolveGlAccountsPayload() },
      { onSuccess: () => setLocalInvoice(null) }
    );
  };

  const handlePostToGL = () => {
    if (!id) return;
    const accounts = resolveGlAccountsPayload();
    const isAdvance = inv.invoice_type === 'Advance';
    if (isAdvance) {
      if (!accounts.prepaid_customer_advance_account_id || !accounts.advance_from_customer_account_id) {
        message.error(t('selectPrepaidAndAdvanceAccounts'));
        return;
      }
    } else if (!accounts.ar_account_id) {
      message.error(t('selectArAccountBeforePost'));
      return;
    }
    postToGLMutation.mutate({ id, accounts }, { onSuccess: () => setLocalInvoice(null) });
  };

  const handleSaveAddresses = () => {
    if (!id) return;
    updateAddressesMutation.mutate(
      {
        id,
        customer_address: billingAddressDraft,
        shipping_address: shippingAddressDraft,
      },
      {
        onSuccess: () => setAddressesDirty(false),
      }
    );
  };
  const showUniversalApproval = !!inv.approval_instance_id;
  const isSubmittedInv = inv.invoice_status === 'Submitted';

  const columns: ColumnsType<SalesInvoiceItem | Partial<SalesInvoiceItem>> = [
    { title: '#', dataIndex: 'line_number', key: 'line_number', width: 60, align: 'center' },
    { title: t('item'), dataIndex: 'item_description', key: 'item_description', ellipsis: true },
    {
      title: t('revision'),
      dataIndex: 'revision',
      key: 'revision',
      width: 80,
      render: (v: string) => v || '—',
    },
    { title: t('qty'), dataIndex: 'quantity', key: 'quantity', width: 80, align: 'right' },
    {
      title: t('unitPrice'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 110,
      align: 'right',
      render: (v: number) => (v != null ? fmt(Number(v)) : '-'),
    },
    {
      title: t('netAmount'),
      dataIndex: 'net_amount',
      key: 'net_amount',
      width: 110,
      align: 'right',
      render: (v: number) => (v != null ? fmt(Number(v)) : '-'),
    },
    {
      title: t('revenueAccount'),
      key: 'revenue_account_id',
      width: 220,
      render: (_: unknown, record: Partial<SalesInvoiceItem>, index: number) => {
        const rid = record.revenue_account_id ?? '';
        const acc = incomeAccounts.find((a) => a.id === rid);
        if (canEdit) {
          return (
            <Select
              value={rid || undefined}
              onChange={(val) => handleLineRevenueChange(index, val ?? '')}
              placeholder={t('selectAccount')}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="label"
              options={incomeAccounts.map((a) => ({ value: a.id, label: `${a.account_code} - ${a.account_name}` }))}
            />
          );
        }
        return acc ? `${acc.account_code} - ${acc.account_name}` : '-';
      },
    },
  ];

  type AccountOption = { id: string; account_code: string; account_name: string };
  const findAccount = (accountId: string, options: AccountOption[]): AccountOption | undefined =>
    options.find((a) => a.id === accountId) ??
    [...assetAccounts, ...liabilityAccounts, ...incomeAccounts].find((a) => a.id === accountId);

  const renderAccountSelect = (
    value: string | undefined,
    options: AccountOption[],
    onChange: (id: string | undefined) => void
  ) => {
    const selected = value && !options.some((a) => a.id === value) ? findAccount(value, options) : undefined;
    const list = selected ? [selected, ...options] : options;
    return (
      <Select
        value={value || undefined}
        onChange={onChange}
        placeholder={t('selectAccount')}
        style={{ width: '100%' }}
        allowClear
        showSearch
        optionFilterProp="label"
        options={list.map((a) => ({ value: a.id, label: `${a.account_code} - ${a.account_name}` }))}
      />
    );
  };

  const formatAccountLabel = (accountId: string | null | undefined, options: AccountOption[]) => {
    if (!accountId) return '—';
    const acc = findAccount(accountId, options);
    return acc ? `${acc.account_code} - ${acc.account_name}` : accountId;
  };

  const headerActions = (
    <Space wrap size="small">
      <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/invoices')}>
        {tc('back')}
      </Button>
      {canEdit && (
        <Button type="primary" onClick={handleSave} disabled={!isEditMode} loading={updateMutation.isPending}>
          {tc('save')}
        </Button>
      )}
      {isDraft && (
        <>
          <Button
            loading={siSubmitLoading}
            onClick={async () => {
              setSiSubmitLoading(true);
              try {
                await salesInvoiceService.submitForApproval(id!);
                message.success(t('submittedForApproval'));
                queryClient.invalidateQueries({ queryKey: ['sales-invoices', id] });
                queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
              } catch (e: any) {
                message.error(e?.response?.data?.message || e?.message || t('submitFailed'));
              } finally {
                setSiSubmitLoading(false);
              }
            }}
          >
            {t('submitForApproval')}
          </Button>
          {!showUniversalApproval && (
            <Button icon={<CheckCircleOutlined />} onClick={() => approveMutation.mutate(id!)} loading={approveMutation.isPending}>
              {tc('approve')}
            </Button>
          )}
          <Button
            icon={<SendOutlined />}
            onClick={() => postMutation.mutate(id!)}
            loading={postMutation.isPending}
            disabled={inv.approval_status !== 'Approved'}
          >
            {tc('post')}
          </Button>
        </>
      )}
      {isSubmittedInv && !showUniversalApproval && (
        <Button icon={<CheckCircleOutlined />} onClick={() => approveMutation.mutate(id!)} loading={approveMutation.isPending}>
          {tc('approve')}
        </Button>
      )}
      {!inv.gl_posted && inv.invoice_status !== 'Cancelled' && (
        <Button
          icon={<AccountBookOutlined />}
          onClick={handlePostToGL}
          loading={postToGLMutation.isPending}
        >
          {t('postToGl')}
        </Button>
      )}
      {inv.gl_posted && inv.invoice_status !== 'Cancelled' && (
        <Popconfirm
          title={t('unpostFromGlConfirm')}
          onConfirm={() => unpostFromGLMutation.mutate(id!)}
          okText={t('unpost')}
          cancelText={tc('cancel')}
        >
          <Button icon={<AccountBookOutlined />} loading={unpostFromGLMutation.isPending} danger>
            {t('unpostFromGl')}
          </Button>
        </Popconfirm>
      )}
      {inv.gl_posted && inv.invoice_status !== 'Cancelled' && (
        <>
          <Button
            icon={<QrcodeOutlined />}
            loading={uaeEinvoiceLoading}
            disabled={uaeGenerateDisabled}
            title={
              uaeReadinessError
                ? t('ftaReadinessCheckFailed')
                : uaeReadiness && !uaeReadiness.ready
                  ? t('completeFtaReadinessBeforeGenerate')
                  : undefined
            }
            onClick={async () => {
              if (!id) return;
              setUaeEinvoiceLoading(true);
              try {
                await complianceService.generateUaeEInvoice(id);
                message.success(t('uaeEinvoiceGenerated'));
                queryClient.invalidateQueries({ queryKey: ['sales-invoices', id] });
                queryClient.invalidateQueries({ queryKey: ['uae-einvoice-readiness', id] });
                refetchUaeTransmissions();
              } catch (e: any) {
                message.error(e?.response?.data?.message || e?.message || t('uaeEinvoiceFailed'));
              } finally {
                setUaeEinvoiceLoading(false);
              }
            }}
          >
            {t('generateUaeEinvoice')}
          </Button>
          {(inv.qr_code_data || latestUaeTransmission?.qr_payload) && (
            <Button icon={<QrcodeOutlined />} onClick={() => setUaeQrModalVisible(true)}>
              {t('viewQr')}
            </Button>
          )}
        </>
      )}
      {inv.invoice_status !== 'Cancelled' && (
        <>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'preprinted',
                  label: t('printPreprinted'),
                  icon: <PrinterOutlined />,
                  onClick: () => handlePrint('preprinted'),
                },
                {
                  key: 'letterhead',
                  label: t('printLetterhead'),
                  icon: <PrinterOutlined />,
                  onClick: () => handlePrint('letterhead'),
                },
              ] as MenuProps['items'],
            }}
            trigger={['click']}
          >
            <Button type="default" icon={<PrinterOutlined />}>
              {t('print')} <DownOutlined />
            </Button>
          </Dropdown>
          <ReportDocumentActions
            reportKey="sales-invoice"
            params={{ id: inv.id }}
            documentId={inv.id}
            sendPermissions={['growth.invoice.edit', 'growth.invoice.create']}
            emailLabel={t('emailCustomer')}
          />
          <Button icon={<FileTextOutlined />} onClick={handlePrintVoucher}>
            {t('printVoucher')}
          </Button>
        </>
      )}
      {inv.invoice_status !== 'Cancelled' && inv.invoice_status !== 'Draft' && (
        <>
          <Button
            icon={<DollarOutlined />}
            onClick={() =>
              navigate(
                `/fin/receipt-vouchers/new?customer_id=${encodeURIComponent(inv.customer_id || '')}&receipt_type=AgainstInvoice`
              )
            }
            disabled={inv.payment_status === 'Paid'}
            title={t('receivePaymentHint')}
          >
            {t('receivePayment')}
          </Button>
          <Popconfirm title={t('cancelInvoiceConfirm')} onConfirm={() => cancelMutation.mutate(id!)} okText={tc('yes')} cancelText={tc('no')}>
            <Button icon={<StopOutlined />} danger disabled={inv.invoice_status === 'Paid'}>
              {tc('cancel')}
            </Button>
          </Popconfirm>
        </>
      )}
    </Space>
  );

  const soId = (inv.salesOrder as any)?.id;
  const rightRail = (
    <div>
      <Title level={5}>{t('quickLinks')}</Title>
      <Space direction="vertical" style={{ width: '100%' }}>
        {soId && (
          <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate(`/growth/orders/${soId}`)}>
            {t('customerOrder')}
          </Button>
        )}
        <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/growth/invoices')}>
          {t('allInvoices')}
        </Button>
        <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/fin/reports/customer-outstanding')}>
          {t('customerOutstanding')}
        </Button>
      </Space>
      <Divider />
      <Title level={5}>{t('uaeEinvoice')}</Title>
      {inv.gl_posted && inv.invoice_status !== 'Cancelled' && uaeReadinessError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('ftaReadinessCheckFailed')}
          description={
            (uaeReadinessQueryError as { response?: { data?: { message?: string } } })?.response?.data
              ?.message || t('couldNotVerifyFtaFields')
          }
        />
      )}
      {inv.gl_posted && inv.invoice_status !== 'Cancelled' && uaeReadiness && (
        <div style={{ marginBottom: 12 }}>
          <Alert
            type={uaeReadiness.ready ? 'success' : 'warning'}
            showIcon
            message={uaeReadiness.ready ? t('ftaReadinessComplete') : t('ftaReadinessIncomplete')}
            description={
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {uaeReadiness.groups.map((g) => (
                  <li key={g.id}>
                    {g.ready ? '✓' : '✗'} {g.label}
                    {!g.ready && g.missing.length > 0 && (
                      <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                        {g.missing[0]}
                      </Text>
                    )}
                  </li>
                ))}
              </ul>
            }
          />
        </div>
      )}
      {latestUaeTransmission ? (
        <Descriptions column={1} size="small">
          <Descriptions.Item label={t('status')}>
            <Tag color={latestUaeTransmission.status === 'Cleared' ? 'green' : 'default'}>
              {latestUaeTransmission.status}
            </Tag>
          </Descriptions.Item>
          {latestUaeTransmission.clearance_number && (
            <Descriptions.Item label={t('clearanceNumber')}>{latestUaeTransmission.clearance_number}</Descriptions.Item>
          )}
          {latestUaeTransmission.exchange_uuid && (
            <Descriptions.Item label={t('uuid')}>{latestUaeTransmission.exchange_uuid}</Descriptions.Item>
          )}
        </Descriptions>
      ) : (
        <Text type="secondary">{t('generateAfterGlPost')}</Text>
      )}
    </div>
  );

  return (
    <div style={{ margin: '-20px -20px 0' }}>
      {id ? <AiPageContextSync module="sales" docType="SalesInvoice" docId={id} /> : null}
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel={t('taxInvoice')}
            documentNumber={inv.invoice_number}
            statusTag={
              <Space wrap>
                <Tag color={inv.invoice_status === 'Draft' ? 'default' : inv.invoice_status === 'Paid' ? 'green' : 'blue'}>
                  {inv.invoice_status ? invoiceStatusLabel(inv.invoice_status, t) : inv.invoice_status}
                </Tag>
                <Tag color={inv.payment_status === 'Paid' ? 'green' : 'default'}>
                  {paymentStatusLabel(inv.payment_status ?? 'Unpaid', t)}
                </Tag>
                {inv.invoice_type === 'Advance' && <Tag color="purple">{t('advance')}</Tag>}
                {inv.gl_posted && <Tag color="green">{t('glPostedTag')}</Tag>}
              </Space>
            }
            subtitle={
              <Text type="secondary">{inv.invoice_date ? dayjs(inv.invoice_date).format('DD MMM YYYY') : ''}</Text>
            }
            facts={
              <Space wrap>
                <Text>
                  <strong>{t('customer')}:</strong> {inv.customer_name ?? '—'}
                </Text>
                <Text>
                  <strong>{t('balanceDue')}:</strong>{' '}
                  {computedBalanceDue != null ? fmt(computedBalanceDue) : '—'}
                </Text>
              </Space>
            }
            primaryActions={headerActions}
          />
        }
        workflow={<WorkflowStrip stages={siWorkflowStages} current={siWorkflowCurrent} compact />}
        rightRail={rightRail}
      >
        <TaskRoutingCard
          sourceDocumentType="SalesInvoice"
          sourceDocumentId={inv.id}
          nextActionUserId={(inv as { next_action_user_id?: string | null }).next_action_user_id}
          documentOwnerLabel={t('createdBy')}
          documentOwnerUserId={(inv as { created_by?: string | null }).created_by}
          disabled={inv.invoice_status === 'Cancelled' || inv.invoice_status === 'Posted'}
          invalidateQueryKeys={[['sales-invoices', id!]]}
        />
        {showUniversalApproval && (
          <div style={{ marginBottom: 16 }}>
            <ApprovalPanel
              entityType={ENTITY_TYPE_SALES_INVOICE}
              entityId={inv.id}
              onApproveViaDocument={async (remarks) => {
                await salesInvoiceService.approve(inv.id, remarks);
                queryClient.invalidateQueries({ queryKey: ['sales-invoices', id] });
                queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
              }}
              onRejectViaDocument={async (payload) => {
                await salesInvoiceService.rejectApproval(inv.id, payload);
                queryClient.invalidateQueries({ queryKey: ['sales-invoices', id] });
                queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
              }}
              extraInvalidateQueryKeys={[['sales-invoices', id], ['sales-invoices']]}
            />
          </div>
        )}
        <Collapse
          defaultActiveKey={['header', 'lines']}
          items={[
            {
              key: 'header',
              label: t('invoiceSettlementGlAccounts'),
              children: (
                <div>
      <Card title={t('header')} style={{ marginBottom: 16, border: 0 }} styles={{ body: { padding: 0 } }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label={t('customer')}>{inv.customer_name ?? '-'}</Descriptions.Item>
          <Descriptions.Item label={t('department')}>
            {isDraft ? (
              <DepartmentSelect
                style={{ minWidth: 220 }}
                value={(localInvoice?.department_id ?? inv.department_id) ?? undefined}
                onChange={(v) => setLocalInvoice((prev) => ({ ...prev, department_id: v ?? null }))}
              />
            ) : inv.department_id
              ? (() => {
                  const d = departments.find((x) => x.id === inv.department_id);
                  return d ? `${d.department_code} - ${d.department_name}` : inv.department_id;
                })()
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('invoiceDate')}>
            {isDraft ? (
              <DatePicker
                allowClear={false}
                style={{ minWidth: 160 }}
                value={
                  (localInvoice?.invoice_date ?? inv.invoice_date)
                    ? dayjs(localInvoice?.invoice_date ?? inv.invoice_date)
                    : undefined
                }
                onChange={(d) =>
                  setLocalInvoice((prev) => ({
                    ...prev,
                    invoice_date: d ? d.format('YYYY-MM-DD') : inv.invoice_date,
                  }))
                }
              />
            ) : inv.invoice_date ? (
              dayjs(inv.invoice_date).format('DD MMM YYYY')
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('dueDate')}>{inv.due_date ? dayjs(inv.due_date).format('DD MMM YYYY') : '-'}</Descriptions.Item>
          <Descriptions.Item label={t('invoiceType')}>{invoiceTypeLabel(inv.invoice_type, t)}</Descriptions.Item>
          <Descriptions.Item label={t('soNumber')}>{(inv.salesOrder as any)?.sales_order_number ?? '-'}</Descriptions.Item>
          <Descriptions.Item label={t('deliveryNotes')}>
            {(() => {
              const dns =
                inv.delivery_notes?.length
                  ? inv.delivery_notes
                  : inv.delivery_note_number
                    ? [{ id: inv.delivery_note_id, delivery_note_number: inv.delivery_note_number }]
                    : [];
              if (!dns.length) return '—';
              return (
                <Space size={[4, 4]} wrap>
                  {dns.map((dn, idx) => (
                    <React.Fragment key={dn.id ?? dn.delivery_note_number}>
                      {idx > 0 ? <span>,</span> : null}
                      {dn.id ? (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: 'auto' }}
                          onClick={() => navigate(`/growth/delivery-notes/${dn.id}`)}
                        >
                          {dn.delivery_note_number}
                        </Button>
                      ) : (
                        <span>{dn.delivery_note_number}</span>
                      )}
                    </React.Fragment>
                  ))}
                </Space>
              );
            })()}
          </Descriptions.Item>
          <Descriptions.Item label={t('customerPo')}>
            {isDraft ? (
              <Input
                allowClear
                style={{ minWidth: 180 }}
                placeholder={t('customerPo')}
                value={
                  localInvoice?.customer_po_number !== undefined
                    ? localInvoice.customer_po_number ?? ''
                    : inv.customer_po_number?.trim() ||
                      (inv.salesOrder as { customer_po_number?: string })?.customer_po_number ||
                      ''
                }
                onChange={(e) =>
                  setLocalInvoice((prev) => ({
                    ...prev,
                    customer_po_number: e.target.value.trim() ? e.target.value : null,
                  }))
                }
              />
            ) : (
              inv.customer_po_number?.trim() ||
              (inv.salesOrder as { customer_po_number?: string })?.customer_po_number ||
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('customerPoDate')}>
            {isDraft ? (
              <DatePicker
                allowClear
                style={{ minWidth: 160 }}
                value={(() => {
                  const d =
                    localInvoice?.customer_po_date !== undefined
                      ? localInvoice.customer_po_date
                      : inv.customer_po_date ??
                        (inv.salesOrder as { customer_po_date?: string })?.customer_po_date;
                  return d ? dayjs(d) : undefined;
                })()}
                onChange={(d) =>
                  setLocalInvoice((prev) => ({
                    ...prev,
                    customer_po_date: d ? d.format('YYYY-MM-DD') : null,
                  }))
                }
              />
            ) : (() => {
              const d = inv.customer_po_date ?? (inv.salesOrder as { customer_po_date?: string })?.customer_po_date;
              return d ? dayjs(d).format('DD MMM YYYY') : '-';
            })()}
          </Descriptions.Item>
          <Descriptions.Item label={t('revenueServiceType')}>{invoiceRstLabel ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={t('currency')}>{inv.currency ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={t('subtotal')}>{inv.subtotal != null ? fmt(Number(inv.subtotal)) : '-'}</Descriptions.Item>
          <Descriptions.Item label={t('vatTax')}>{(inv.vat_amount != null && Number(inv.vat_amount) !== 0) ? fmt(Number(inv.vat_amount)) : '-'}</Descriptions.Item>
          <Descriptions.Item label={t('grandTotal')}>{inv.grand_total != null ? fmt(Number(inv.grand_total)) : '-'}</Descriptions.Item>
          <Descriptions.Item label={t('paidAmount')}>{inv.paid_amount != null ? fmt(Number(inv.paid_amount)) : '-'}</Descriptions.Item>
          <Descriptions.Item label={t('advanceAdjusted')}>{inv.advance_adjusted_amount != null ? fmt(Number(inv.advance_adjusted_amount)) : '-'}</Descriptions.Item>
          <Descriptions.Item label={t('creditNoteAdjusted')}>{inv.credit_note_adjusted_amount != null ? fmt(Number(inv.credit_note_adjusted_amount)) : '-'}</Descriptions.Item>
          <Descriptions.Item label={t('balanceDueLabel')}>
            {computedBalanceDue != null ? fmt(computedBalanceDue) : '-'}
          </Descriptions.Item>
        </Descriptions>

      <Divider orientation="left">{t('billingAndShipping')}</Divider>
      <div style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }} wrap>
          <Text type="secondary">
            {t('billingShippingHint')}
          </Text>
          {canEditAddresses ? (
            <Button
              type="primary"
              size="small"
              loading={updateAddressesMutation.isPending}
              disabled={!addressesDirty}
              onClick={handleSaveAddresses}
            >
              {t('saveAddresses')}
            </Button>
          ) : null}
        </Space>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Text strong>{t('billingAddress')}</Text>
            <TextArea
              rows={3}
              style={{ marginTop: 8 }}
              value={billingAddressDraft}
              disabled={!canEditAddresses}
              onChange={(e) => {
                setBillingAddressDraft(e.target.value);
                setAddressesDirty(true);
              }}
            />
          </Col>
          <Col xs={24} md={12}>
            <Text strong>{t('shippingAddress')}</Text>
            <TextArea
              rows={3}
              style={{ marginTop: 8 }}
              value={shippingAddressDraft}
              disabled={!canEditAddresses}
              onChange={(e) => {
                setShippingAddressDraft(e.target.value);
                setAddressesDirty(true);
              }}
            />
          </Col>
        </Row>
      </div>

        {showSettlement && (
          <>
            {settlementSourcesCustomerId &&
              settlementPendingInfo.showAlert &&
              !loadingOpenAdvances &&
              !loadingOpenCreditNotes && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={t('settlementAvailable')}
                  description={
                    <span>
                      {t('settlementAvailableDesc', {
                        amount: fmt(settlementPendingInfo.remainingCreditHeadroom),
                      })}{' '}
                      {settlementPendingInfo.advanceSourceCount > 0 && (
                        <span>
                          {t('advanceSourceCount', { count: settlementPendingInfo.advanceSourceCount })}
                          {settlementPendingInfo.creditNoteSourceCount > 0 ? '; ' : '.'}
                        </span>
                      )}
                      {settlementPendingInfo.creditNoteSourceCount > 0 && (
                        <span>
                          {t('creditNoteSourceCount', { count: settlementPendingInfo.creditNoteSourceCount })}.
                        </span>
                      )}
                    </span>
                  }
                />
              )}
            <Divider orientation="left">{t('settlement')}</Divider>
            {(advanceAllocations.length > 0 || creditNoteAllocations.length > 0) ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                {advanceAllocations.length > 0 && (
                  <div>
                    <Space style={{ marginBottom: 8 }}>
                      <Text strong>{t('appliedCustomerAdvances')}</Text>
                      <Button type="primary" size="small" onClick={() => setApplyAdvanceModalVisible(true)}>
                        {t('applyCustomerAdvances')}
                      </Button>
                    </Space>
                    <Table
                      size="small"
                      dataSource={advanceAllocations.map((a) => ({ ...a, key: a.id }))}
                      columns={[
                        {
                          title: t('source'),
                          key: 'src',
                          width: 160,
                          render: (_: unknown, row: { receiptVoucher?: { receipt_number?: string }; advanceSalesInvoice?: { invoice_number?: string } }) =>
                            row.receiptVoucher?.receipt_number ?? row.advanceSalesInvoice?.invoice_number ?? '—',
                        },
                        { title: t('date'), dataIndex: 'adjustment_date', key: 'adj_date', width: 120, render: (v: string) => v ? dayjs(v).format('DD-MMM-YYYY') : '—' },
                        { title: t('amount'), dataIndex: 'allocated_amount', key: 'amount', width: 120, align: 'right' as const, render: (v: number) => fmt(Number(v)) },
                      ]}
                      pagination={false}
                    />
                  </div>
                )}
                {creditNoteAllocations.length > 0 && (
                  <div>
                    <Space style={{ marginBottom: 8 }}>
                      <Text strong>{t('appliedCreditNotes')}</Text>
                      <Button type="primary" size="small" onClick={() => setApplyCreditNoteModalVisible(true)}>
                        {t('applyCreditNotes')}
                      </Button>
                    </Space>
                    <Table
                      size="small"
                      dataSource={creditNoteAllocations.map((a) => ({ ...a, key: a.id }))}
                      columns={[
                        { title: t('creditNoteNo'), dataIndex: ['customerCreditNote', 'credit_note_number'], key: 'cn_number', width: 140 },
                        { title: t('date'), dataIndex: 'adjustment_date', key: 'adj_date', width: 120, render: (v: string) => v ? dayjs(v).format('DD-MMM-YYYY') : '—' },
                        { title: t('amount'), dataIndex: 'allocated_amount', key: 'amount', width: 120, align: 'right' as const, render: (v: number) => fmt(Number(v)) },
                      ]}
                      pagination={false}
                    />
                  </div>
                )}
                {advanceAllocations.length === 0 && creditNoteAllocations.length === 0 && (
                  <Space>
                    <Button type="default" size="small" onClick={() => setApplyAdvanceModalVisible(true)}>
                      {t('applyCustomerAdvances')}
                    </Button>
                    <Button type="default" size="small" onClick={() => setApplyCreditNoteModalVisible(true)}>
                      {t('applyCreditNotes')}
                    </Button>
                  </Space>
                )}
              </Space>
            ) : (
              <Space>
                <Button type="default" size="small" onClick={() => setApplyAdvanceModalVisible(true)}>
                  {t('applyCustomerAdvances')}
                </Button>
                <Button type="default" size="small" onClick={() => setApplyCreditNoteModalVisible(true)}>
                  {t('applyCreditNotes')}
                </Button>
              </Space>
            )}
          </>
        )}

        {canEditGlAccounts && (
          <>
            <Divider orientation="left">{t("glAccountsForPost")}</Divider>
            {!isDraft && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={
                  inv.invoice_type === 'Advance'
                    ? t('glAccountsAdvanceHint')
                    : t('glAccountsArHint')
                }
              />
            )}
            <Row gutter={16}>
              {inv.invoice_type === 'Advance' ? (
                <>
                  <Col span={12}>
                    <Text strong>{t("prepaidCustomerAdvanceAccount")}</Text>
                    <div style={{ marginTop: 4 }}>
                      {renderAccountSelect(
                        (localInvoice?.prepaid_customer_advance_account_id ??
                          inv.prepaid_customer_advance_account_id) ?? undefined,
                        liabilityAccounts,
                        (v) =>
                          setLocalInvoice((prev) => ({
                            ...prev,
                            prepaid_customer_advance_account_id: v || undefined,
                          }))
                      )}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text strong>{t("advanceFromCustomerAccount")}</Text>
                    <div style={{ marginTop: 4 }}>
                      {renderAccountSelect(
                        (localInvoice?.advance_from_customer_account_id ??
                          inv.advance_from_customer_account_id) ?? undefined,
                        assetAccounts,
                        (v) =>
                          setLocalInvoice((prev) => ({
                            ...prev,
                            advance_from_customer_account_id: v || undefined,
                          }))
                      )}
                    </div>
                  </Col>
                  <Col span={12} style={{ marginTop: 12 }}>
                    <Text strong>{t("vatAccount")}</Text>
                    <div style={{ marginTop: 4 }}>
                      {renderAccountSelect(
                        (localInvoice?.vat_account_id ?? inv.vat_account_id) ?? undefined,
                        liabilityAccounts,
                        (v) => setLocalInvoice((prev) => ({ ...prev, vat_account_id: v || undefined }))
                      )}
                    </div>
                  </Col>
                </>
              ) : (
                <>
                  <Col span={12}>
                    <Text strong>{t("arAccount")}</Text>
                    <div style={{ marginTop: 4 }}>
                      {renderAccountSelect(
                        (localInvoice?.ar_account_id ?? inv.ar_account_id) ?? undefined,
                        assetAccounts,
                        (v) => setLocalInvoice((prev) => ({ ...prev, ar_account_id: v || undefined }))
                      )}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text strong>{t("vatAccount")}</Text>
                    <div style={{ marginTop: 4 }}>
                      {renderAccountSelect(
                        (localInvoice?.vat_account_id ?? inv.vat_account_id) ?? undefined,
                        liabilityAccounts,
                        (v) => setLocalInvoice((prev) => ({ ...prev, vat_account_id: v || undefined }))
                      )}
                    </div>
                  </Col>
                  <Col span={12} style={{ marginTop: 12 }}>
                    <Text strong>{t("advanceAdjustmentAccount")}</Text>
                    <div style={{ marginTop: 4 }}>
                      {renderAccountSelect(
                        (localInvoice?.advance_adjustment_account_id ??
                          inv.advance_adjustment_account_id) ?? undefined,
                        liabilityAccounts,
                        (v) =>
                          setLocalInvoice((prev) => ({
                            ...prev,
                            advance_adjustment_account_id: v || undefined,
                          }))
                      )}
                    </div>
                  </Col>
                  <Col span={12} style={{ marginTop: 12 }}>
                    <Text strong>{t("retentionAccount")}</Text>
                    <div style={{ marginTop: 4 }}>
                      {renderAccountSelect(
                        (localInvoice?.retention_account_id ?? inv.retention_account_id) ?? undefined,
                        liabilityAccounts,
                        (v) =>
                          setLocalInvoice((prev) => ({ ...prev, retention_account_id: v || undefined }))
                      )}
                    </div>
                  </Col>
                </>
              )}
            </Row>
            {!isDraft && (
              <div style={{ marginTop: 12 }}>
                <Button
                  type="default"
                  onClick={handleSaveGlAccounts}
                  loading={updateGlAccountsMutation.isPending}
                >
                  {t("saveGlAccounts")}
                </Button>
              </div>
            )}
          </>
        )}
        {inv.gl_posted && (
          <>
            <Divider orientation="left">{t("glAccounts")}</Divider>
            <Descriptions size="small" column={2}>
              {inv.invoice_type === 'Advance' ? (
                <>
                  <Descriptions.Item label={t("prepaidCustomerAdvance")}>
                    {formatAccountLabel(inv.prepaid_customer_advance_account_id, liabilityAccounts)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("advanceFromCustomer")}>
                    {formatAccountLabel(inv.advance_from_customer_account_id, assetAccounts)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("vatAccount")}>
                    {formatAccountLabel(inv.vat_account_id, liabilityAccounts)}
                  </Descriptions.Item>
                </>
              ) : (
                <>
                  <Descriptions.Item label={t("arAccount")}>
                    {formatAccountLabel(inv.ar_account_id, assetAccounts)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("vatAccount")}>
                    {formatAccountLabel(inv.vat_account_id, liabilityAccounts)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("advanceAdjustment")}>
                    {formatAccountLabel(inv.advance_adjustment_account_id, liabilityAccounts)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("retention")}>
                    {formatAccountLabel(inv.retention_account_id, liabilityAccounts)}
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>
          </>
        )}
        {(inv.salesOrder as any) && (
          <>
            <Divider orientation="left">{t("advanceRetentionFromOrder")}</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>{t("advanceReversed")}</Text>
                <div style={{ marginTop: 4 }}>
                  <Text>{(inv.salesOrder as any)?.advance_reversed_amount != null ? fmt(Number((inv.salesOrder as any).advance_reversed_amount)) : '-'}</Text>
                </div>
              </Col>
              <Col span={12}>
                <Text strong>{t("retentionWithheld")}</Text>
                <div style={{ marginTop: 4 }}>
                  <Text>{(inv.salesOrder as any)?.retention_withheld_amount != null ? fmt(Number((inv.salesOrder as any).retention_withheld_amount)) : '-'}</Text>
                </div>
              </Col>
            </Row>
          </>
        )}
      </Card>
                </div>
              ),
            },
            {
              key: 'lines',
              label: t('lineItems'),
              children: (
                <div>
      <Card
        title={t("lineItemsTitle")}
        extra={
          canEdit && (
            <Space>
              <Select
                placeholder={t("defaultRevenueAccount")}
                value={defaultRevenueAccountId || undefined}
                onChange={setDefaultRevenueAccountId}
                style={{ width: 260 }}
                allowClear
                showSearch
                optionFilterProp="label"
                options={incomeAccounts.map((a) => ({ value: a.id, label: `${a.account_code} - ${a.account_name}` }))}
              />
              <Button onClick={handleApplyDefaultRevenue}>{t("applyToAllLines")}</Button>
            </Space>
          )
        }
      >
        <Table
          rowKey={(r) => r.id ?? r.line_number ?? Math.random()}
          columns={columns}
          dataSource={displayItems}
          loading={loadingItems}
          pagination={false}
          size="small"
        />
      </Card>
                </div>
              ),
            },
            {
              key: 'attachments',
              label: t('attachments'),
              children: (
                <>
                  <EntityAttachmentsCard
                    entityType={DOCUMENT_ENTITY_TYPE_SALES_INVOICE}
                    entityId={inv.id}
                    title={t("attachments")}
                    readOnly={inv.invoice_status === 'Cancelled'}
                    queryKeyPrefix={['documents', 'sales-invoice']}
                    uploadHint={SCM_ATTACHMENT_UPLOAD_HINT}
                  />
                  <InheritedAttachmentsCard
                    trailDomain="sales"
                    entityType={DOCUMENT_ENTITY_TYPE_SALES_INVOICE}
                    entityId={inv.id}
                    queryKeyPrefix={['documents', 'sales-invoice']}
                  />
                </>
              ),
            },
          ]}
        />
      </WorkspaceLayout>

      <Modal
        title={t('applyCustomerAdvances')}
        open={applyAdvanceModalVisible}
        onCancel={() => { setApplyAdvanceModalVisible(false); setAdvanceAmounts({}); }}
        onOk={async () => {
          if (!id) return;
          const allocations: ApplyAdvanceAllocationInput[] = openAdvances
            .filter((r) => (advanceAmounts[r.id] ?? 0) > 0)
            .map((r): ApplyAdvanceAllocationInput =>
              r.source === 'advance_invoice'
                ? { advance_sales_invoice_id: r.id, amount: advanceAmounts[r.id] ?? 0 }
                : { receipt_voucher_id: r.id, amount: advanceAmounts[r.id] ?? 0 }
            );
          if (allocations.length === 0) {
            message.warning(t('enterAtLeastOneAmount'));
            return;
          }
          try {
            await applyAdvancesMutation.mutateAsync({ invoiceId: id, allocations });
            setApplyAdvanceModalVisible(false);
            setAdvanceAmounts({});
          } catch (e) {
            // error in mutation
          }
        }}
        okText={t('apply')}
        confirmLoading={applyAdvancesMutation.isPending}
        destroyOnClose
        width={560}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {t('applyAdvancesHint')}
        </Text>
        {openAdvances.length === 0 ? (
          <Text>{t('noOpenAdvances')}</Text>
        ) : (
          <Table
            size="small"
            dataSource={openAdvances.map((r) => ({ ...r, key: r.id }))}
            pagination={false}
            columns={
              [
                {
                  title: t('type'),
                  key: 'src_type',
                  width: 110,
                  render: (_: unknown, row: OpenAdvanceRow) =>
                    row.source === 'advance_invoice' ? t('advanceInvoice') : t('receipt'),
                },
                { title: t('referenceNo'), dataIndex: 'receipt_number', key: 'receipt_number', width: 120 },
                { title: t('date'), dataIndex: 'receipt_date', key: 'date', width: 100 },
                {
                  title: t('openBalance'),
                  dataIndex: 'open_balance',
                  key: 'open_balance',
                  width: 100,
                  align: 'right' as const,
                  render: (v: number) => fmt(v),
                },
                {
                  title: t('amountToApply'),
                  key: 'amount',
                  width: 140,
                  render: (_: unknown, row: { id: string; open_balance: number }) => (
                    <InputNumber
                      min={0}
                      max={row.open_balance}
                      value={advanceAmounts[row.id] ?? undefined}
                      onChange={(v) => setAdvanceAmounts((prev) => ({ ...prev, [row.id]: Number(v) ?? 0 }))}
                      style={{ width: '100%' }}
                    />
                  ),
                },
              ] as ColumnsType<(typeof openAdvances)[number]>
            }
          />
        )}
      </Modal>

      <Modal
        title={t('uaeEinvoiceQr')}
        open={uaeQrModalVisible}
        onCancel={() => setUaeQrModalVisible(false)}
        footer={[<Button key="close" onClick={() => setUaeQrModalVisible(false)}>{tc('close')}</Button>]}
        width={420}
      >
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          {latestUaeTransmission?.qr_image_data_url ? (
            <img
              src={latestUaeTransmission.qr_image_data_url}
              alt={t('uaeEinvoiceQrAlt')}
              width={280}
              height={280}
              style={{ objectFit: 'contain' }}
            />
          ) : (
            <QRCode
              value={inv.qr_code_data || latestUaeTransmission?.qr_payload || ''}
              size={280}
              level="M"
              includeMargin
              renderAs="svg"
            />
          )}
        </div>
      </Modal>

      <Modal
        title={t('applyCreditNotes')}
        open={applyCreditNoteModalVisible}
        onCancel={() => { setApplyCreditNoteModalVisible(false); setCreditNoteAmounts({}); }}
        onOk={async () => {
          if (!id) return;
          const allocations = openCreditNotes
            .filter((r) => (creditNoteAmounts[r.id] ?? 0) > 0)
            .map((r) => ({ customer_credit_note_id: r.id, amount: creditNoteAmounts[r.id] ?? 0 }));
          if (allocations.length === 0) {
            message.warning(t('enterAtLeastOneAmount'));
            return;
          }
          try {
            await applyCreditNotesMutation.mutateAsync({ invoiceId: id, allocations });
            setApplyCreditNoteModalVisible(false);
            setCreditNoteAmounts({});
          } catch (e) {
            // error in mutation
          }
        }}
        okText={t('apply')}
        confirmLoading={applyCreditNotesMutation.isPending}
        destroyOnClose
        width={560}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {t('applyCreditNotesHint')}
        </Text>
        {openCreditNotes.length === 0 ? (
          <Text>{t('noOpenCreditNotes')}</Text>
        ) : (
          <Table
            size="small"
            dataSource={openCreditNotes.map((r) => ({ ...r, key: r.id }))}
            pagination={false}
            columns={[
              { title: t('creditNoteNo'), dataIndex: 'credit_note_number', key: 'credit_note_number', width: 120 },
              { title: t('date'), dataIndex: 'credit_note_date', key: 'date', width: 100 },
              { title: t('openBalance'), dataIndex: 'open_balance', key: 'open_balance', width: 100, align: 'right' as const, render: (v: number) => fmt(v) },
              {
                title: t('amountToApply'),
                key: 'amount',
                width: 140,
                render: (_: unknown, row: { id: string; open_balance: number }) => (
                  <InputNumber
                    min={0}
                    max={row.open_balance}
                    value={creditNoteAmounts[row.id] ?? undefined}
                    onChange={(v) => setCreditNoteAmounts((prev) => ({ ...prev, [row.id]: Number(v) ?? 0 }))}
                    style={{ width: '100%' }}
                  />
                ),
              },
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default SalesInvoiceDetailPage;
